import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    setFilters,
    setSelectedDate,
    setVisibleDateRange,
} from 'src/pages/home/redux/actions/index';
import {
    TimelineWrapper,
    TimelineContainer,
    WeekdayHeader,
    WeekDayIndicator,
    CaseListingContainer,
    ListingMain,
    TextDemi,
    TextRegular,
    StyledWeekDate,
} from './Timeline.styled.jsx';
import TimelineNavigation from './TimelineNavigation';
import TimelineCaseColumn from './TimelineCaseColumn';
import Plus from 'src/assets/images/Add.svg';
import Button from 'src/common/refactoredComponents/button/Button';
import { ButtonStyles } from 'src/constants/buttonStyles';
import { convertDateToISOString, getRoutePathWithParams, logErrorToConsole } from 'src/utils/util';
import { getEndDateForStartDate } from 'src/utils/dateUtils';
import Spinner from 'src/common/refactoredComponents/spinner/Spinner';
import { useHistory } from 'react-router-dom';
import OrderDetails from 'src/pages/orders/components/orderDetails/OrderDetails';
import { closeOrderSummaryModalState } from 'src/pages/orders/redux/actions';
import { useTranslation } from 'react-i18next';
import {
    getPageVisibilityForUser,
    getUser,
} from 'src/pages/equity/components/common/utils/eventHandlerUtil.jsx';
import { PAGE_VISIBILITY_RULE } from 'src/constants/appConstants.js';
import { resetNotes, setIsMastOrder, setPediatricCheck } from 'src/common/redux/actions/index.js';
import {
    resetBomData,
    reSetProgressBarStatus,
    resetSurgeryDetails,
    resetSurgeryDetailsReducer,
    resetShipToAddress,
    resetCreateOrUpdateSurgery,
} from 'src/pages/scheduleSurgery/redux/actions';
import { useEffectWithErrorLogging } from 'src/utils/useEffectWithErrorLogging';

const Timeline = (props) => {
    const { t } = useTranslation();
    const days = [
        t('more.common.setsAndItems.Su'),
        t('more.common.setsAndItems.Mo'),
        t('more.common.setsAndItems.Tu'),
        t('more.common.setsAndItems.We'),
        t('more.common.setsAndItems.Th'),
        t('more.common.setsAndItems.Fr'),
        t('more.common.setsAndItems.Sa'),
    ];
    const [timelineDates, setTimelineDates] = useState([]);
    const dispatch = useDispatch();
    const history = useHistory();
    const selectedDate = useSelector(
        (state) => state.filterReducer.selectedDate
    );
    const dateRange = useSelector(
        (state) => state.filterReducer.visibleDateRange
    );
    const businessDays = useSelector(
        (state) => state.filterReducer.lastThreeBusinessDays
    );

    const cardDataLoading = useSelector(
        (state) => state.ordersReducer.cardDataLoading
    );

    const bannerHeight = useSelector(
        (state) => state.loginReducer.bannerHeight
    );
    const { createOrderDetails } = useSelector((state) => state.loginReducer);

    const [caseCount, setCaseCount] = useState(0);

    useEffectWithErrorLogging(() => {
        generateTimelineDates();
    }, [dateRange]);

    useEffectWithErrorLogging(() => {
        setSelectedDateRange();
    }, [selectedDate]);

    useEffectWithErrorLogging(() => {
        calculateNoOfCasesForWeek();
    });

    useEffectWithErrorLogging(() => {
        return () => {
            const newEndDate =
                new Date(selectedDate).setHours(0, 0, 0, 0) >=
                new Date().setHours(0, 0, 0, 0)
                    ? getEndDateForStartDate(new Date(selectedDate), 364)
                    : getEndDateForStartDate(new Date(selectedDate), 0);
            dispatch(
                setFilters({
                    startDate: convertDateToISOString(
                        new Date(selectedDate),
                        'start'
                    ),
                    endDate: convertDateToISOString(
                        new Date(newEndDate),
                        'end'
                    ),
                })
            );
        };
    }, [selectedDate]);

    const enableOrderButtonBasedOnUser = () => {
        let showButton = false;
        const pageVisibilityRule = getPageVisibilityForUser(getUser());
        pageVisibilityRule.map((page) => {
            if (
                page &&
                page.pageRule.name === PAGE_VISIBILITY_RULE.name &&
                page.pageRule.rule === PAGE_VISIBILITY_RULE.rule
            ) {
                showButton = true;
            }
        }); 
        return showButton;
    };

    //dispatcher functions for timeline component
    const visibleDateRangeDispatcher = (startDate, endDate) => {
        //takes in date objects and dispatches to redux
        dispatch(
            setVisibleDateRange([
                new Date(startDate.setHours(0, 0, 0, 0)),
                new Date(endDate.setHours(23, 59, 59, 59)),
            ])
        );
    };

    const changeSelectedDate = (date) => {
        //takes in date object and dispatches to redux
        dispatch(setSelectedDate(new Date(date.setHours(0, 0, 0, 0))));
    };
    //dispatcher functions end here

    const generateTimelineDates = () => {
        //based on date range from redux generates an array of objects of format {day:<Su/Mo>, value:<date object>, dateINMMM:}
        try {
            let newTimeLineDates = [];
            for (let range = 0; range < 7; range++) {
                let dateToPush = new Date(dateRange[0]);
                dateToPush.setDate(dateToPush.getDate() + range);
                newTimeLineDates.push({
                    day: days[range],
                    value: dateToPush,
                    dateInDDMMM: `${new Intl.DateTimeFormat('en', {
                        day: '2-digit',
                    }).format(dateToPush)} ${new Intl.DateTimeFormat('en', {
                        month: 'short',
                    }).format(dateToPush)}`,
                });
            }
            setTimelineDates(newTimeLineDates);
        } catch (error) {
            logErrorToConsole(error);
        }
    };

    const setSelectedDateRange = () => {
        const currentDay = new Date(selectedDate).getDay();
        const startWeekDate = new Date(selectedDate);
        const endWeekDate = new Date(selectedDate);
        startWeekDate.setDate(new Date(selectedDate).getDate() - currentDay);
        endWeekDate.setDate(
            new Date(selectedDate).getDate() + (6 - currentDay)
        );
        visibleDateRangeDispatcher(startWeekDate, endWeekDate);
        dispatch(
            setFilters({
                startDate: convertDateToISOString(
                    new Date(startWeekDate),
                    'start'
                ),
                endDate: convertDateToISOString(new Date(endWeekDate), 'end'),
            })
        );
    };

    const calculateNoOfCasesForWeek = () => {
        let surgeryCards = document.getElementsByClassName('week-view-cards');
        let countOfCases = 0;
        for (let surgeryCard in surgeryCards) {
            let childNodeList = surgeryCards[surgeryCard].childNodes || [];
            if (childNodeList.length > 0) {
                countOfCases = countOfCases + 1;
            }
        }
        setCaseCount(countOfCases);
    };

    const getBackGroundColor = (date) => { 
        const today = new Date();
        if (
            date.getDate() == today.getDate() &&
            date.getMonth() == today.getMonth() &&
            date.getFullYear() == today.getFullYear()
        ) {
            return '#FFEBBF 0% 0% no-repeat padding-box';
        }
    };

    const getBorderColor = (date) => {
        const today = new Date();
        if (
            date.getDate() == today.getDate() &&
            date.getMonth() == today.getMonth() &&
            date.getFullYear() == today.getFullYear()
        ) {
            return '1px solid #EBD9B1';
        } else if (
            date.getDate() == new Date(selectedDate).getDate() &&
            date.getMonth() == new Date(selectedDate).getMonth() &&
            date.getFullYear() == new Date(selectedDate).getFullYear()
        ) {
            return '1px solid #F26522';
        }
    };
    const getWeekNumber = (dateGiven) => {
        let firstJan = new Date(dateGiven.getFullYear(), 0, 1);
        let weekNo = Math.ceil(
            ((dateGiven - firstJan) / 86400000 + firstJan.getDay() + 1) / 7
        );
        return weekNo;
    };
    const showTodayButton = () => {
        let currentDate = new Date();
        let currentDateWeekNumber = getWeekNumber(currentDate);
        let selectedDateWeekNumber = getWeekNumber(new Date(selectedDate));
        if (
            currentDateWeekNumber !== selectedDateWeekNumber ||
            new Date(selectedDate).getFullYear() !== currentDate.getFullYear()
        ) {
            return true;
        } else {
            return false;
        }
    };

    const showAddSurgeryButton = (columnDate) => {
        let columnDateAtMidnight = new Date(columnDate.setHours(0, 0, 0, 0));
        // console.log(
        //     threeDaysBefore.valueOf(),
        //     new Date(selectedDate).valueOf(),
        //     date.valueOf()
        // );
        // if (
        //     (businessDays.includes(columnDateAtMidnight.valueOf()) ||
        //         columnDateAtMidnight.valueOf() >= currentDate.valueOf()) &&
        //     columnDateAtMidnight.valueOf() === new Date(selectedDate).valueOf()
        // ) {
        //     return true;
        // } else {
        //     return false;
        // }
        if (
            (businessDays.includes(columnDateAtMidnight.valueOf()) ||
                columnDateAtMidnight.valueOf() >= businessDays[0]) &&
            columnDateAtMidnight.valueOf() ===
                new Date(selectedDate).valueOf() &&
            enableOrderButtonBasedOnUser() &&
            createOrderDetails.scheduleSurgery
        ) {
            return true;
        } else {
            return false;
        }
    };

    const { orderSummaryModal } = useSelector(
        (state) => state.orderSummaryReducer
    );

    const closeCardPreviewHandler = () => {
        dispatch(
            closeOrderSummaryModalState({
                myCaseRef: '',
                orderSummaryModal: false,
            })
        );
        props.openSearchModal();
    };

    return (
        <TimelineWrapper bannerMargin={bannerHeight}>
            <TimelineContainer>
                <TimelineNavigation
                    selectedDateDispatcher={changeSelectedDate}
                    showTodayButton={showTodayButton()}
                    caseCount={caseCount}
                />
                <ListingMain>
                    <WeekdayHeader>
                        {timelineDates.map((timelineDate, index) => {
                            return (
                                <WeekDayIndicator
                                    key={index}
                                    onClick={() => {
                                        try {
                                            changeSelectedDate(timelineDate.value);
                                        } catch (error) {
                                            logErrorToConsole(error);
                                        }
                                    }}
                                    background={getBackGroundColor(
                                        timelineDate.value
                                    )}
                                    border={getBorderColor(timelineDate.value)}
                                >
                                    <TextRegular fontSize={'16px'}>
                                        {timelineDate.day}
                                    </TextRegular>
                                    <TextDemi
                                        fontSize={'16px'}
                                        margin={'0 10px 0 10px'}
                                    >
                                        {timelineDate.dateInDDMMM}
                                    </TextDemi>
                                    <StyledWeekDate>
                                        <a
                                            href="/scheduleSurgery"
                                            style={{ textDecoration: 'none' }}
                                            onClick={(e) => {
                                                try {
                                                    e.preventDefault();
                                                    sessionStorage.removeItem('myCaseRefID');
                                                    dispatch(setIsMastOrder(false));
                                                    dispatch(reSetProgressBarStatus());
                                                    dispatch(resetSurgeryDetailsReducer());
                                                    dispatch(resetShipToAddress());
                                                    dispatch(resetCreateOrUpdateSurgery());
                                                    dispatch(setPediatricCheck(''));
                                                    dispatch(resetSurgeryDetails());
                                                    dispatch(resetBomData());
                                                    dispatch(resetNotes());
                                                    localStorage.removeItem('alertDatesforConsignmentAdd');
                                                    history.push(
                                                        getRoutePathWithParams(
                                                            '/scheduleSurgery'
                                                        )
                                                    );
                                                } catch (error) {
                                                    logErrorToConsole(error);
                                                }
                                            }}
                                        >
                                            <Button
                                            id={'common-timeline-add-surgery'}
                                                show={showAddSurgeryButton(
                                                    timelineDate.value
                                                )}
                                                //surgery={true}
                                                variant={ButtonStyles?.surgery}
                                                width={'19px'}
                                                height={'19px'}
                                                borderRadius={'4px'}
                                                // clicked={}
                                            >
                                                <img
                                                    src={Plus}
                                                    width="auto"
                                                    height="auto"
                                                ></img>
                                            </Button>
                                        </a>
                                    </StyledWeekDate>
                                </WeekDayIndicator>
                            );
                        })}
                    </WeekdayHeader>
                    {!cardDataLoading ? (
                        <CaseListingContainer>
                            {timelineDates.map((timelineDate) => {
                                return (
                                    <TimelineCaseColumn
                                        key={timelineDate.value}
                                        value={timelineDate.value}
                                        setCaseCount={setCaseCount}
                                    />
                                );
                            })}
                        </CaseListingContainer>
                    ) : (
                        <Spinner
                            id="common-timeline-timeline-timelineSpinner"
                            animation="border"
                            variant="primary"
                            small
                        ></Spinner>
                    )}
                </ListingMain>
            </TimelineContainer>
            <OrderDetails
                showOrderListDetails={orderSummaryModal}
                orderListDetailsClose={closeCardPreviewHandler}
                fromCalendar={true}
                fromListScreen={false}
            />
        </TimelineWrapper>
    );
};

export default Timeline;
